'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useLocale } from '@/i18n/context';
import { type Locale, dictionaries } from '@/i18n/dictionaries';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export function I18nSwitcher() {
  const { locale, setLocale } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const locales: Locale[] = ['en', 'fa', 'tr', 'ar'];

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-transparent hover:bg-[#206A4F]/10 dark:hover:bg-white/10 text-foreground text-xs sm:text-sm font-extrabold transition-all duration-200 active:scale-95 shadow-xs"
        aria-expanded={isOpen}
        title="Change Language"
      >
        <Globe size={18} className="text-[#206A4F] dark:text-emerald-400 shrink-0" />
        <span className="uppercase font-bold tracking-wide">{locale}</span>
        <ChevronDown size={14} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            
            className="absolute z-50 min-w-[170px] ltr:right-0 rtl:left-0 bg-white dark:bg-zinc-900 rounded-2xl p-2 border border-[#206A4F]/15 dark:border-zinc-800 shadow-xl backdrop-blur-xl 
                       bottom-full mb-2 origin-bottom 
                       md:bottom-auto md:top-full md:mb-0 md:mt-2 md:origin-top"
          >
            <div className="space-y-1">
              {locales.map((loc) => {
                const item = dictionaries[loc];
                const isSelected = loc === locale;
                return (
                  <button
                    key={loc}
                    onClick={() => {
                      setLocale(loc);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-extrabold transition-colors duration-150 ${
                      isSelected
                        ? 'bg-[#206A4F]/15 dark:bg-emerald-500/20 text-[#206A4F] dark:text-emerald-400 font-black'
                        : 'text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800/80'
                    }`}
                  >
                    <span className="flex items-center gap-2.5">
                      <span className="text-base leading-none">{item.flag}</span>
                      <span>{item.nativeName}</span>
                    </span>
                    {isSelected && <Check size={16} className="text-[#206A4F] dark:text-emerald-400 ml-2 rtl:mr-2 rtl:ml-0" />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}